<template>
  <div class="min-h-screen w-full bg-white text-foreground relative">
    <header class="absolute top-0 left-0 p-10">
      <img :src="logoLast" alt="Last.app" class="h-10 w-auto" />
    </header>

    <main class="min-h-screen flex items-center justify-center px-6">
      <l-form
        ref="form"
        @submit="startLogin"
        :validation-schema="formSchema"
        v-slot="{ validate, meta }"
        class="w-full max-w-sm flex flex-col gap-8"
      >
        <h1 class="heading-1 text-foreground">
          {{ t('login.title') }}
        </h1>

        <div class="flex flex-col gap-5">
          <l-form-field name="email" :initial-value="''" v-slot="{ field }">
            <l-form-label :label="t('inputs.email-label')" />
            <l-input
              v-bind="field"
              type="text"
              :placeholder="t('inputs.email-label')"
              autocomplete="email"
            />
            <l-form-error />
          </l-form-field>

          <l-form-field name="password" :initial-value="''" v-slot="{ field }">
            <l-form-label :label="t('inputs.password-label')" />
            <l-password-input v-bind="field" :placeholder="t('inputs.password-placeholder')" />
            <l-form-error />
          </l-form-field>
        </div>

        <l-button
          class="w-full"
          size="medium"
          :loading="loggingIn"
          :disabled="!meta.value.valid"
          @click="() => validate()"
        >
          {{ t('login.title') }}
        </l-button>
      </l-form>
    </main>
  </div>
</template>

<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod'
import { storeToRefs } from 'pinia'
import { onMounted, ref, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import zod from 'zod'

import {
  LButton,
  LForm,
  LFormError,
  LFormField,
  LFormLabel,
  LInput,
  LPasswordInput
} from '@last/core-ui/paprika'

import logoLast from '@/assets/logo-last.svg'
import { useAuthStore } from '@/store/auth'

const router = useRouter()
const route = useRoute()
const { t } = useI18n()

const auth = useAuthStore()
const { isAuthenticated } = storeToRefs(auth)

const loggingIn = ref(false)

const schema = zod.object({
  email: zod.string().email(),
  password: zod.string().min(1)
})
type FormValues = zod.infer<typeof schema>
const formSchema = toTypedSchema(schema)
const form = useTemplateRef('form')

onMounted(() => {
  if (isAuthenticated.value) {
    router.push({ name: 'locationSelector' })
  }
  const qEmail = route.query.email
  if (typeof qEmail === 'string' && qEmail && form.value) {
    form.value.setFieldValue('email', qEmail)
  }
})

async function startLogin({ email, password }: FormValues) {
  form.value?.setFieldError('password', undefined)
  loggingIn.value = true
  try {
    await auth.login({ email, password })
    router.push({ name: 'locationSelector' })
  } catch (_) {
    form.value?.setFieldError('password', t('login.email-or-password-incorrect'))
  }
  loggingIn.value = false
}
</script>
